import React, { useState } from 'react';
import {
    Container,
    Box,
    Stack,
    Typography,
    Paper,
    Pagination,
    CircularProgress,
    Chip,
    Button,
    TextField,
    MenuItem,
} from '@mui/material';
import { AssignmentReturn, OpenInNew } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { useGetMyReturns } from '../../hooks/customer/useGetMyReturns';
import { RETURN_STATUSES } from '../../components/customer/returns/returnConstants';
import { formatMoney } from '../../utils/money';

const Returns = () => {
    const { i18n, t } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('');

    const { returns, meta, isLoading } = useGetMyReturns({
        page,
        per_page: 15,
        status: statusFilter || undefined,
    });
    const lastPage = meta?.last_page || 1;
    const total = meta?.total ?? returns.length;

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={2}
                sx={{ mb: 3 }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <AssignmentReturn fontSize="large" color="primary" />
                    <Box>
                        <Typography variant="h4" fontWeight={800}>
                            {t('returns.title', 'My Returns')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {total} {t('returns.totalReturns', 'returns')}
                        </Typography>
                    </Box>
                </Stack>

                <TextField
                    select
                    size="small"
                    label={t('returns.status', 'Status')}
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPage(1);
                    }}
                    sx={{ minWidth: 180 }}
                >
                    <MenuItem value="">{t('common.all', 'All')}</MenuItem>
                    {Object.keys(RETURN_STATUSES).map((key) => (
                        <MenuItem key={key} value={key}>
                            {isArabic
                                ? RETURN_STATUSES[key].labelAr
                                : RETURN_STATUSES[key].labelEn}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress />
                </Box>
            ) : returns.length === 0 ? (
                <Paper
                    elevation={0}
                    variant="outlined"
                    sx={{ p: { xs: 4, md: 6 }, textAlign: 'center', borderRadius: 3 }}
                >
                    <AssignmentReturn sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        {t('returns.empty', "You haven't requested any returns yet.")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {t(
                            'returns.emptyHint',
                            'Open an order detail and start a return from there.'
                        )}
                    </Typography>
                    <Button variant="contained" onClick={() => navigate('/orders')}>
                        {t('returns.goToOrders', 'Go to my orders')}
                    </Button>
                </Paper>
            ) : (
                <>
                    {returns.map((r) => {
                        const meta = RETURN_STATUSES[r.status] || {
                            color: 'default',
                            labelEn: r.status || '-',
                            labelAr: r.status || '-',
                        };
                        return (
                            <Paper
                                key={r.id}
                                elevation={0}
                                variant="outlined"
                                sx={{
                                    p: 2.5,
                                    mb: 1.5,
                                    borderRadius: 2.5,
                                    cursor: 'pointer',
                                    transition: 'border-color 120ms',
                                    '&:hover': { borderColor: 'primary.main' },
                                }}
                                onClick={() => navigate(`/account/returns/${r.id}`)}
                            >
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    justifyContent="space-between"
                                    spacing={1.5}
                                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                                >
                                    <Box>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                            <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                                                #{r.return_number || r.id}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={isArabic ? meta.labelAr : meta.labelEn}
                                                color={meta.color}
                                                sx={{ fontWeight: 'bold', borderRadius: '6px' }}
                                            />
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('returns.forOrder', 'For order')}
                                            {': '}
                                            <Box component="span" sx={{ fontFamily: 'monospace' }}>
                                                #{r.order?.number || r.order_id}
                                            </Box>
                                            {' · '}
                                            {dayjs(r.requested_at || r.created_at).format('YYYY-MM-DD')}
                                        </Typography>
                                    </Box>

                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                        {r.refund_amount != null && (
                                            <Box textAlign="end">
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {t('returns.refundAmount', 'Refund')}
                                                </Typography>
                                                <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                                                    {formatMoney(r.refund_amount, r.currency)}
                                                </Typography>
                                            </Box>
                                        )}
                                        <OpenInNew fontSize="small" color="action" />
                                    </Stack>
                                </Stack>
                            </Paper>
                        );
                    })}

                    {lastPage > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination
                                count={lastPage}
                                page={page}
                                onChange={(_, value) => {
                                    setPage(value);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                color="primary"
                                shape="rounded"
                            />
                        </Box>
                    )}
                </>
            )}
        </Container>
    );
};

export default Returns;
